import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonInput } from '@ionic/angular';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

@Component({
  selector: 'app-select-search-input',
  templateUrl: './select-search-input.component.html',
  styleUrls: ['./select-search-input.component.scss'],
})
export class SelectSearchInputComponent {
  @ViewChild('inputField', { static: false }) inputField!: IonInput;
  @Input() isRequired: boolean = true;
  @Input() disabled: boolean = false;
  @Input() innerClass: string = '';
  @Input() allData: any = [];
  @Input() label: string = '';
  @Input() selectInput: any = null;
  @Input() selectInputId: any = null;
  @Input() emittOnlyId: boolean = true;
  @Output() selectInputChange = new EventEmitter<any>();
  @Output() selectInputIdChange = new EventEmitter<any>();
  @Output() selectInputoBjectChange = new EventEmitter<any>();
  @Input() helperText: string = '';
  @Input() errorText: string = 'Campo inválido.';
  @Input() isMobile: boolean = false;
  @Input() externalSearch: boolean = false;
  @Output() externalSearchFunction = new EventEmitter<any>();

  filteredData: any = [];
  inputFocus: boolean = false;
  optSelected: boolean = false;
  inputWasChanged: boolean = false;
  lastKeyPressed: string = '';
  canOpenList: boolean = false;
  form: FormGroup;

  private searchSubject = new Subject<string>();

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      input: [{ value: null, disabled: this.disabled }],
    });
  }

  ngOnInit(): void {
    if (this.selectInput) {
      this.form.patchValue({ input: this.selectInput });
    }
    this.filteredData = this.allData;

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: string) => {
        if (this.externalSearch) {
          this.externalSearchFunction.emit(searchTerm);
        } else {
          this.optSelected = false;
          const lowerCaseSearchTerm = searchTerm.toLowerCase();
          this.filteredData = this.allData.filter((data: any) =>
            data.nome.toLowerCase().includes(lowerCaseSearchTerm),
          );
        }

        if (!searchTerm && !this.externalSearch) {
          this.filteredData = this.allData;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['allData']) {
      this.allData = this.allData;
      this.filteredData = this.allData;
    }

    if (changes['disabled'] && this.form) {
      const control = this.form.get('input');
      if (this.disabled) {
        control?.disable({ emitEvent: false });
      } else {
        control?.enable({ emitEvent: false });
      }
    }

    if (changes['selectInput']) {
      if (
        (!this.selectInput || this.selectInput === '') &&
        !this.selectInputId
      ) {
        this.form.patchValue({ input: null });
        this.optSelected = false;
      }
    }
  }

  setFieldValidation() {
    const inputControl = this.form.get('input');

    if (!inputControl) return;

    if (this.isRequired) {
      inputControl.setValidators([Validators.required]);
    } else {
      inputControl.clearValidators();
    }
    inputControl.updateValueAndValidity();
  }

  onKeyDown(event: KeyboardEvent) {
    this.lastKeyPressed = event.key;
  }

  onFocus(event: CustomEvent): void {
    const focusEvent = event.detail;

    if (focusEvent?.relatedTarget?.localName === 'button') {
      setTimeout(() => {
        this.inputField.getInputElement().then((nativeInput) => {
          nativeInput.blur();
        });
      }, 0);
      return;
    }

    if (this.lastKeyPressed === 'Clear') {
      this.lastKeyPressed = '';
      return;
    }

    this.lastKeyPressed = '';
    this.inputFocus = true;
    this.canOpenList = true;
    this.inputWasChanged = false;

    if (!this.form.get('input')?.value) {
      this.filteredData = this.allData;
    }
  }

  onChevronClick() {
    if (this.disabled) return;

    if (this.inputFocus) {
      this.inputFocus = false;
      this.canOpenList = false;
      this.closeList();
    } else {
      this.inputField.setFocus();
      this.lastKeyPressed = '';
      this.inputFocus = true;
      this.inputWasChanged = false;
      this.canOpenList = true;
    }
  }

  onInputChange() {
    this.inputWasChanged = true;
  }

  onBlur(event: CustomEvent) {
    const blurEvent = event.detail as FocusEvent;

    if (!this.inputFocus) {
      return false;
    }

    if (this.lastKeyPressed === 'Tab') {
      return false;
    }

    setTimeout(() => {
      return this.closeList();
    }, 1);

    return '';
  }

  onListBlur(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!relatedTarget || !this.isFocusInsideList(relatedTarget)) {
      return this.closeList();
    }
  }

  clearInput() {
    this.lastKeyPressed = 'Clear';

    this.clearInputAction();

    setTimeout(() => {
      this.inputField.getInputElement().then((nativeInput) => {
        nativeInput.blur();
      });
    }, 0);
  }

  private clearInputAction() {
    this.form.patchValue({ input: null });
    this.selectInput = null;
    this.selectInputId = null;
    this.selectInputChange.emit(null);
    this.selectInputIdChange.emit(null);
    this.selectInputoBjectChange.emit(null);
  }

  private isFocusInsideList(target: HTMLElement): boolean {
    const listElement = document.querySelector('.filtered-list');
    return listElement ? listElement.contains(target) : false;
  }

  private closeList() {
    setTimeout(() => {
      this.inputFocus = false;
      this.canOpenList = false;
      if (this.inputWasChanged && !this.optSelected) {
        this.clearInputAction();
      }
    }, 150);
  }

  onSearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchSubject.next(filterValue);
  }

  selectValue(value: any, event: MouseEvent) {
    event.preventDefault();
    this.optSelected = true;
    this.filteredData = [];
    this.form.patchValue({ input: value.nome });
    this.selectInput = value.nome;
    this.selectInputId = value.id;
    this.selectInputChange.emit(value.nome);
    this.selectInputoBjectChange.emit(value);
    if (this.emittOnlyId) {
      this.selectInputIdChange.emit(value.id);
    } else {
      this.selectInputIdChange.emit(value);
    }
  }

  keydownSelectValue(event: KeyboardEvent) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }
}
